import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { thumbhashToDataUrl } from "@/shared/lib/thumbhash";
import type { VideoJobOutput, VideoJobPhase } from "../hooks/useVideoJobStatus";
import { FilmIcon } from "./icons";

/**
 * The placeholder box matches the shape of the video that's coming: the tool
 * reports the resolved ratio (e.g. "9:16" for vertical UGC) the instant the job
 * is queued, so the loader is sized correctly from the first frame. Once the
 * real output lands we use its exact width/height. Falls back to 16:9.
 */
function aspectRatio(output: VideoJobOutput | null, requestedRatio?: string | null): string {
  if (output?.width && output.height) return `${output.width} / ${output.height}`;
  if (requestedRatio) {
    const [w, h] = requestedRatio.split(":");
    if (w && h) return `${w} / ${h}`;
  }
  return "16 / 9";
}

export interface VideoResultCardProps {
  phase: VideoJobPhase;
  output: VideoJobOutput | null;
  /** Resolved ratio ("16:9" | "1:1" | "9:16" | …) so the loader is sized before the video lands. */
  requestedRatio?: string | null;
}

/**
 * The visual result of an in-chat `generate_video` job: a video-shaped box that
 * starts as a muted skeleton block on a gentle pulse, then fades in the produced
 * video (over its ThumbHash blur) once the job succeeds. The video autoplays
 * muted + looped (with controls) like a preview. On failure/timeout it shows a
 * quiet "couldn't generate" state in the same box. Mirrors `ImageResultCard`.
 */
export function VideoResultCard({ phase, output, requestedRatio }: VideoResultCardProps) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const ratio = aspectRatio(output, requestedRatio);
  const placeholder = thumbhashToDataUrl(output?.thumbhash);
  const src = output?.url;
  const failed = phase === "failed" || errored;
  const ready = Boolean(src) && phase === "succeeded" && !errored;

  return (
    <div
      style={{ aspectRatio: ratio }}
      className="relative w-full max-w-[340px] overflow-hidden rounded-md bg-canvas-soft"
    >
      {placeholder && !failed ? (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          className={cn(
            "absolute inset-0 size-full scale-110 object-cover blur-2xl transition-opacity duration-300",
            loaded ? "opacity-0" : "opacity-100",
          )}
        />
      ) : null}

      {ready && src ? (
        <video
          src={src}
          poster={output?.thumbnail_url || undefined}
          autoPlay
          loop
          muted
          playsInline
          controls
          onLoadedData={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            "absolute inset-0 size-full object-cover transition-opacity duration-300",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      ) : null}

      {phase === "generating" && !failed ? (
        <span className="absolute inset-0 animate-pulse bg-tint" aria-hidden="true" />
      ) : null}

      {failed ? (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-3 text-center text-ink-faint [&_svg]:size-6">
          <FilmIcon />
          <span className="font-sans text-[11.5px]">{t("chat.toolConfirm.videoFailed")}</span>
        </span>
      ) : null}
    </div>
  );
}
